import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { FaFacebookF } from "react-icons/fa";
import axios from "axios";

const FB_APP_ID = process.env.REACT_APP_FB_APP_ID;
const API_BASE = process.env.REACT_APP_API_URL;

const LoginFacebook = ({ onAuthComplete }) => {
  const navigate = useNavigate();
  const { facebookLogin } = useAuth();
  const [sdkReady, setSdkReady] = useState(!!window.FB);
  const [loading, setLoading] = useState(false);
  const [reaskedOnce, setReaskedOnce] = useState(false);

  // Load SDK once
  useEffect(() => {
    if (window.FB) return setSdkReady(true);

    window.fbAsyncInit = function () {
      window.FB.init({
        appId: FB_APP_ID,
        cookie: true,
        xfbml: false,
        version: "v20.0",
      });
      setSdkReady(true);
    };

    const id = "facebook-jssdk";
    if (!document.getElementById(id)) {
      const js = document.createElement("script");
      js.id = id;
      js.src = "https://connect.facebook.net/en_US/sdk.js";
      js.async = true;
      const fjs = document.getElementsByTagName("script")[0];
      fjs.parentNode.insertBefore(js, fjs);
    }
  }, []);

  // Single place to process ALL FB login responses
  const processFacebookResponse = async (resp) => {
    try {
      // USER CANCELLED OR POPUP CLOSED
      if (!resp?.authResponse) {
        onAuthComplete?.({
          success: false,
          error: "Facebook login was cancelled by the user.",
          cancelled: true,
        });
        return;
      }

      const { accessToken, userID } = resp.authResponse;
      const data = await facebookLogin(accessToken, userID);

      if (data.success) {
        if (data.token) localStorage.setItem("token", data.token);

        onAuthComplete?.({
          success: true,
          email: data.user?.email,
          isNewUser: false,
          user: data.user,
          status: data.user?.approvalStatus,
          message: data.message || "Logged in with Facebook",
          token: data.token,
        });
        return;
      }

      if (data?.isNewUser) {
        onAuthComplete?.({
          success: true,
          email: data.user?.email,
          isNewUser: true,
          user: data.user,
          status: data.user?.approvalStatus,
          message: data.message || "Your account is awaiting admin approval.",
        });
        return;
      }

      if (
        !data?.isNewUser &&
        data?.user &&
        data?.meta?.approvalStatus &&
        data.meta.approvalStatus !== "approved"
      ) {
        onAuthComplete?.({
          success: true,
          email: data.user?.email,
          isNewUser: false,
          user: data.user,
          status: data.meta.approvalStatus,
          message: data.message || "Your account is awaiting admin approval.",
        });
        return;
      }

      // missing email => rerequest once
      if (!data.success && data?.meta?.missing === "email" && !reaskedOnce) {
        setReaskedOnce(true);
        setLoading(false);
        window.FB.login(
          (resp2) => {
            processFacebookResponse(resp2);
          },
          {
            scope: "email,public_profile",
            return_scopes: true,
            auth_type: "rerequest",
          }
        );
        return;
      }

      const msg = data?.message || "Facebook authentication failed";
      onAuthComplete?.({
        success: false,
        error: msg,
        isNewUser: false,
      });
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Facebook authentication failed.";
      onAuthComplete?.({
        success: false,
        error: msg,
        isNewUser: false,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFacebookLogin = () => {
    if (!window.FB || !sdkReady || loading) return;
    setLoading(true);

    window.FB.login(
      (resp) => {
        // async handler lives in one place
        processFacebookResponse(resp);
      },
      { scope: "email,public_profile", return_scopes: true }
    );
  };

  return (
    <div className="w-full flex justify-end">
      <button
        onClick={handleFacebookLogin}
        className="w-12 h-12 flex items-center justify-center rounded-lg bg-[#d9d9d9] cursor-pointer"
        disabled={!sdkReady || loading}
      >
        <FaFacebookF fill="#787878" size={20} />
      </button>
    </div>
  );
};

export default LoginFacebook;
